import { useMemo } from 'react';

import { useQuery } from '@apollo/client/react';
import { Alert, Button } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-premium';
import {
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import {
  Order_By,
  type Property_Transactions_Bool_Exp,
  type Property_Transactions_Order_By,
} from '@graphql/__generated__/graphql';
import { type QuickFilterDefinition } from '@modules/_shared/components/filters/Filters';
import { ListToolbar } from '@modules/_shared/components/list-toolbar/ListToolbar';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { TransactionsList } from '@modules/_shared/components/transactions/TransactionsList';
import { useBulkEditMode } from '@modules/_shared/hooks/useBulkEditMode';
import { useBulkPublishTransactions } from '@modules/_shared/hooks/useBulkPublishTransactions';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  getIncludedIds,
  isEmptySelection,
} from '@modules/_shared/utils/gridRowSelection';
import {
  parseOrderByParam,
  withTiebreaker,
} from '@modules/_shared/utils/listUrlParams';
import { useScopedSearchParams } from '@modules/_shared/utils/navigation';
import { getPaginationVariables } from '@modules/_shared/utils/paginations';
import { prepareWhereClauseQuery } from '@modules/_shared/utils/parseWhereClause';
import { TEAM_USER_FILTER } from '@modules/_shared/utils/quickFilters';
import { TransactionCreateDrawer } from '@modules/transactions/TransactionCreateDrawer';
import { TransactionDrawer } from '@modules/transactions/TransactionDrawer';
import {
  GET_PROPERTY_TRANSACTIONS,
  GET_PROPERTY_TRANSACTIONS_LIST_COUNT,
} from '@modules/transactions/transactionQueries';
import { useLocale } from '@shared/hooks/locale';

const SCOPE = 'team-transactions';

export const TeamTransactions = () => {
  const { t } = useLocale();
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [scopedSearchParams] = useScopedSearchParams(SCOPE);
  const [searchParams] = useSearchParams();
  const { me } = useAppData();
  const isAdmin = me?.is_admin;

  const myTeamsIds = useMemo(
    () => me?.teams?.map(team => team.team_id) ?? [],
    [me?.teams],
  );

  const { editMode, selectedRows, setSelectedRows } =
    useBulkEditMode<GridRowSelectionModel>(undefined, SCOPE);
  const includedSelectedRows = getIncludedIds(selectedRows);

  const filterWhere = useMemo(() => {
    try {
      return prepareWhereClauseQuery(
        JSON.parse(scopedSearchParams.get('where') || '{}'),
      );
    } catch (error) {
      console.warn('Invalid where parameter in URL, ignoring filters:', error);
      return {};
    }
  }, [scopedSearchParams]);

  const where: Property_Transactions_Bool_Exp = useMemo(
    () => ({
      _and: [
        {
          _or: [
            { seller_broker_team_id: { _eq: teamId } },
            { buyer_broker_team_id: { _eq: teamId } },
          ],
        },
        filterWhere,
      ],
    }),
    [teamId, filterWhere],
  );

  const orderByParam = scopedSearchParams.get('order_by');
  const order_by = useMemo(
    () =>
      withTiebreaker<Property_Transactions_Order_By>(
        parseOrderByParam<Property_Transactions_Order_By>(orderByParam, [
          { created_at: Order_By.Desc },
          { signature_date: Order_By.Desc },
        ]),
        { id: Order_By.Asc },
        'id',
      ),
    [orderByParam],
  );

  const { limit, offset } = getPaginationVariables(scopedSearchParams);

  const variables = useMemo(
    () => ({
      where,
      order_by,
      team_ids: myTeamsIds,
      limit,
      offset,
      includeValuations: me?.can_edit_settings ?? false,
    }),
    [where, order_by, myTeamsIds, limit, offset, me?.can_edit_settings],
  );

  const { data, loading, error } = useQuery(GET_PROPERTY_TRANSACTIONS, {
    variables,
  });

  const { data: countData } = useQuery(GET_PROPERTY_TRANSACTIONS_LIST_COUNT, {
    variables: { where },
  });

  const refetchQueries = useMemo(
    () => [
      { query: GET_PROPERTY_TRANSACTIONS, variables },
      { query: GET_PROPERTY_TRANSACTIONS_LIST_COUNT, variables: { where } },
    ],
    [variables, where],
  );

  const {
    apolloError,
    setErrorLike,
    skippedMessage,
    setSkippedMessage,
    bulkPublishLoading,
    bulkUnpublishLoading,
    bulkUpdateLoading,
    handleBulkPublish,
  } = useBulkPublishTransactions({
    selectedRows,
    setSelectedRows,
    refetchQueries,
  });

  const quickFilters = useMemo<QuickFilterDefinition[]>(
    () => [
      {
        label: t('Signature date'),
        path: ['property_transactions_bool_exp', 'signature_date'],
      },
      {
        label: t('Seller agent'),
        path: ['property_transactions_bool_exp', 'seller_broker'],
        filter: TEAM_USER_FILTER,
      },
      {
        label: t('Place'),
        path: ['property_transactions_bool_exp', 'property', 'places'],
      },
      {
        label: t('Purchase price'),
        path: ['property_transactions_bool_exp', 'purchase_price'],
      },
      {
        label: t('Approved'),
        path: ['property_transactions_bool_exp', 'reviewed_at'],
        nullCheckMode: 'not-null',
      },
      {
        label: t('Published'),
        path: ['property_transactions_bool_exp', 'is_published'],
      },
    ],
    [t],
  );

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  return (
    <>
      <ListToolbar
        tableName="property_transactions"
        queryParamsScope={SCOPE}
        newLink={
          isAdmin
            ? {
                pathname: 'new/scratch',
                search: searchParams.toString(),
              }
            : undefined
        }
        quickFilters={quickFilters}
        bulkActions={
          isAdmin && (
            <>
              <Button
                disableElevation
                disabled={
                  isEmptySelection(selectedRows) ||
                  includedSelectedRows == null ||
                  bulkUpdateLoading
                }
                loading={bulkPublishLoading}
                loadingPosition="start"
                onClick={() => handleBulkPublish(true)}
              >
                {t('Publish')}
              </Button>
              <Button
                disableElevation
                disabled={
                  isEmptySelection(selectedRows) ||
                  includedSelectedRows == null ||
                  bulkUpdateLoading
                }
                loading={bulkUnpublishLoading}
                loadingPosition="start"
                onClick={() => handleBulkPublish(false)}
              >
                {t('Unpublish')}
              </Button>
            </>
          )
        }
      />
      <TransactionsList
        transactions={data?.property_transactions}
        loading={loading}
        displayPropertyInfo
        showReviews
        checkboxSelection={editMode}
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={setSelectedRows}
        extraDataGridProps={{
          rowCount:
            countData?.property_transactions_aggregate?.aggregate?.count ?? 0,
          queryParamsScope: SCOPE,
          disableRowSelectionOnClick: editMode,
        }}
      />
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
      {skippedMessage != null && (
        <RASnackbar
          message={skippedMessage}
          severity="warning"
          onClose={() => setSkippedMessage(null)}
        />
      )}
      <Routes>
        <Route
          path="new/*"
          element={
            <TransactionCreateDrawer
              refetchQueries={refetchQueries}
              onCreate={id => {
                navigate({
                  pathname: `./${id}/transaction`,
                  search: searchParams.toString(),
                });
              }}
            />
          }
        />
        <Route
          path=":transactionId/*"
          element={<TransactionDrawer refetchQueries={refetchQueries} />}
        />
      </Routes>
    </>
  );
};
